import { t } from '../../i18n';
import { downloadSpreadsheetMl, type ExcelCell } from '../../utils/exportExcelXml';
import { formatDateTime, formatMoney } from '../../utils/format';
import { escapeHtml, printHtmlDocument } from '../../utils/printDocument';
import {
  emptyCells,
  excelHeaderRow,
  htmlHeaderRow,
  htmlSheetFooter,
  chunkPages,
} from '../customers/exportCustomerShared';

export type ReportMetaLine = { label: string; value: string };

export type ReportExcelSection = {
  title?: string;
  headers: string[];
  isNumeric: (index: number) => boolean;
  dataRows: ExcelCell[][];
  totalRow?: ExcelCell[];
};

export type ReportPdfSection = {
  title?: string;
  headers: string[];
  isNumeric: (index: number) => boolean;
  rowHtml: string[];
  totalsRowHtml?: string;
};

export function reportHeaderRowIndex(
  metaCount: number,
  hasSectionTitle = false,
): number {
  return 3 + metaCount + (metaCount > 0 ? 1 : 0) + (hasSectionTitle ? 1 : 0) + 1;
}

export function wrapSummaryHtml(...cards: string[]): string {
  if (cards.length === 0) return '';
  return `<div class="summary">${cards.join('')}</div>`;
}

export function reportSummaryCard(
  title: string,
  invoice: string,
  cash: string,
  total: string,
  invoiceLabel: string = t().financial.invoice,
  cashLabel: string = t().financial.cash,
): string {
  return `
    <div class="summary-card">
      <div class="label">${escapeHtml(title)}</div>
      <div class="row"><span>${escapeHtml(invoiceLabel)}</span><span>${escapeHtml(formatMoney(invoice))}</span></div>
      <div class="row"><span>${escapeHtml(cashLabel)}</span><span>${escapeHtml(formatMoney(cash))}</span></div>
      <div class="row"><span>${escapeHtml(t().financial.grandTotal)}</span><span>${escapeHtml(formatMoney(total))}</span></div>
    </div>`;
}

export function downloadReportExcel(input: {
  filename: string;
  title: string;
  meta?: ReportMetaLine[];
  columnCount: number;
  columnWidths: number[];
  sections: ReportExcelSection[];
}): void {
  const labels = t().customers;
  const printedAt = formatDateTime(new Date());
  const merge = Math.max(0, input.columnCount - 1);
  const meta = input.meta ?? [];

  const rows: ExcelCell[][] = [
    [{ type: 'string', value: 'BalanceFlow', style: 'Title', mergeAcross: merge }],
    [{ type: 'string', value: input.title, style: 'Subtitle', mergeAcross: merge }],
    emptyCells(1),
  ];

  for (const line of meta) {
    rows.push([
      { type: 'string', value: line.label, style: 'Label' },
      {
        type: 'string',
        value: line.value,
        mergeAcross: Math.max(0, merge - 1),
      },
    ]);
  }
  if (meta.length > 0) rows.push(emptyCells(1));

  let firstHeaderRow: number | undefined;
  let firstDataLastRow: number | undefined;
  const firstSection = input.sections[0];

  for (const section of input.sections) {
    if (section.title) {
      rows.push([
        {
          type: 'string',
          value: section.title,
          style: 'Subtitle',
          mergeAcross: merge,
        },
      ]);
    }
    rows.push(excelHeaderRow(section.headers, section.isNumeric));
    if (firstHeaderRow == null) firstHeaderRow = rows.length;
    rows.push(...section.dataRows);
    if (firstDataLastRow == null && firstSection === section) {
      firstDataLastRow =
        section.dataRows.length > 0
          ? (firstHeaderRow ?? rows.length) + section.dataRows.length
          : firstHeaderRow;
    }
    if (section.totalRow) rows.push(section.totalRow);
    rows.push(emptyCells(1));
  }

  rows.push([
    {
      type: 'string',
      value: `${labels.printedAt}: ${printedAt}`,
      style: 'Footer',
      mergeAcross: merge,
    },
  ]);

  downloadSpreadsheetMl(input.filename, {
    sheetName: input.title,
    columnWidths: input.columnWidths,
    rows,
    freezeRows: firstHeaderRow,
    autoFilter:
      input.sections.length === 1 &&
      firstSection &&
      firstSection.dataRows.length > 0 &&
      firstHeaderRow != null &&
      firstDataLastRow != null
        ? {
            headerRow: firstHeaderRow,
            lastDataRow: firstDataLastRow,
            columns: input.columnCount,
          }
        : undefined,
    headerLeft: `BalanceFlow — ${input.title}`,
    footerLeft: `${labels.printedAt}: ${printedAt}`,
    footerRight: `${labels.page} &P ${labels.pageOf} &N`,
  });
}

export function printReportPdf(input: {
  title: string;
  meta?: ReportMetaLine[];
  summaryHtml?: string;
  sections: ReportPdfSection[];
  firstPageRows?: number;
  nextPageRows?: number;
}): void {
  const printedAt = formatDateTime(new Date());
  const meta = input.meta ?? [];
  const documentTitle = input.title;
  const firstPageRows = input.firstPageRows ?? 16;
  const nextPageRows = input.nextPageRows ?? 24;

  const intro = `
    <h1>BalanceFlow</h1>
    <div class="subtitle">${escapeHtml(input.title)}</div>
    ${
      meta.length
        ? `<div class="meta">${meta
            .map(
              (line) =>
                `<div><span>${escapeHtml(line.label)}:</span> ${escapeHtml(line.value)}</div>`,
            )
            .join('')}</div>`
        : ''
    }
    ${input.summaryHtml ?? ''}`;

  type Sheet = {
    heading: string;
    sectionTitle?: string;
    headers: string[];
    isNumeric: (index: number) => boolean;
    rows: string[];
    totalsRowHtml?: string;
  };

  const sheets: Sheet[] = [];
  let introUsed = false;

  for (const section of input.sections) {
    const pages = chunkPages(
      section.rowHtml,
      introUsed ? nextPageRows : firstPageRows,
      nextPageRows,
    );
    for (let pageIndex = 0; pageIndex < pages.length; pageIndex++) {
      const isLastOfSection = pageIndex === pages.length - 1;
      sheets.push({
        heading: introUsed
          ? `<div class="running-head">${escapeHtml(documentTitle)}</div>`
          : intro,
        sectionTitle: pageIndex === 0 ? section.title : undefined,
        headers: section.headers,
        isNumeric: section.isNumeric,
        rows: pages[pageIndex],
        totalsRowHtml: isLastOfSection ? section.totalsRowHtml : undefined,
      });
      introUsed = true;
    }
  }

  const pageCount = Math.max(sheets.length, 1);
  const html = (sheets.length ? sheets : [
    {
      heading: intro,
      headers: [],
      isNumeric: () => false,
      rows: [],
    } satisfies Sheet,
  ])
    .map((sheet, index) => {
      const table =
        sheet.headers.length > 0
          ? `<table>
              <thead>${htmlHeaderRow(sheet.headers, sheet.isNumeric)}</thead>
              <tbody>${sheet.rows.join('')}</tbody>
              ${sheet.totalsRowHtml ? `<tfoot>${sheet.totalsRowHtml}</tfoot>` : ''}
            </table>`
          : '';
      const sectionTitle = sheet.sectionTitle
        ? `<div class="running-head">${escapeHtml(sheet.sectionTitle)}</div>`
        : '';
      return `
        <section class="sheet">
          ${sheet.heading}
          ${sectionTitle}
          ${table}
          ${htmlSheetFooter(printedAt, index + 1, pageCount)}
        </section>`;
    })
    .join('');

  printHtmlDocument(documentTitle, html);
}
